import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { IAppStore } from '../../../../models/store.model'
import { webApiService } from '@/services'
import { LOGISTIC_BOX_ADMIN_ROLES } from '@/constants/routes'

export interface StoreScope {
  // true si el usuario está restringido a su propia tienda (todos menos admin).
  esScoped: boolean
  // Valor de id_store a mandar al backend:
  //  · admin  → null (ve todas las tiendas del seller)
  //  · resto  → id de su tienda (resuelto vía ti_user_store). Mientras resuelve o
  //             si no tiene tienda asignada → -1 (no filtra de más: no muestra nada).
  scopeStore: number | null
  id_role: number
}

/**
 * Alcance del usuario en el módulo de logística.
 *
 * Solo los roles admin (LOGISTIC_BOX_ADMIN_ROLES) ven todas las tiendas del
 * seller. Cualquier otro rol (custodia, ejecutivo, promotor, jefe de tienda,
 * etc.) queda restringido a su propia tienda. El login NO trae id_store, así que
 * la tienda se resuelve desde ti_user_store con el mismo endpoint que usa el
 * resto del app (getStoreByUser).
 */
export const useStoreScope = (): StoreScope => {
  const user = useSelector((s: IAppStore) => s.user)
  const idRole = user?.id_role ?? 0
  const idUser = user?.id ?? 0
  // Sesión federada (WOM): llega con id 0, así que la tienda NO se resuelve por
  // ti_user_store sino desde la sucursal elegida en el SucursalGate (selectedStore).
  const isSso = user?.sso === true
  const selectedStore = user?.selectedStore ?? (user?.id_store ?? null)
  const esAdmin = LOGISTIC_BOX_ADMIN_ROLES.includes(idRole)
  const esScoped = !esAdmin
  // Para el usuario acotado: -1 = "aún sin resolver / sin tienda" (el backend no
  // devuelve nada hasta tener el id real).
  const [storeId, setStoreId] = useState<number | null>(esScoped ? -1 : null)

  useEffect(() => {
    // SSO resuelve la tienda por selectedStore (no por ti_user_store).
    if (isSso) return
    if (!esScoped || idUser <= 0) { setStoreId(esScoped ? -1 : null); return }
    let alive = true
    void webApiService.getStoreByUser(idUser)
      .then((r: any) => {
        const sid = r?.data?.[0]?.id
        if (alive) setStoreId(sid != null ? Number(sid) : -1)
      })
      .catch(() => { if (alive) setStoreId(-1) })
    return () => { alive = false }
  }, [esScoped, idUser, isSso])

  // WOM (SSO): restringido a su sucursal elegida, tomando el id_store del login
  // federado. -1 mientras no haya elegido sucursal.
  if (isSso) {
    const sid = (selectedStore != null && Number(selectedStore) > 0) ? Number(selectedStore) : -1
    return { esScoped: true, scopeStore: sid, id_role: idRole }
  }

  return { esScoped, scopeStore: esScoped ? storeId : null, id_role: idRole }
}
